import jalali from "jalaali-js";
import { Outputs } from "hooks/useInputHandler";
import { Outputs as FileOutputs } from "hooks/useFileHandler";
import { digitsToHindi, digitsToLatin } from "@bale/utils";
import { formatEpoch } from "./time";
import Bowser from "bowser";
import { utils, writeFile } from "xlsx";

import { strings } from "intl/fa";
import { SponsoredMessageOption } from "core/orderAd/orderAd.entity";
import { MINIMUM_ORDER_EXCEPTION_USER_IDs } from "./constants";
export enum LocalStorageKeys {
  AccessToken = "AccessToken",
  Staging = "staging",
  TransactionHash = "TransactionHash",
}

export const isProductionMode = (): boolean =>
  window.location.href.includes("tabligh.bale.ai") ||
  window.location.href.includes("tabligh-staging.bale.ai");

export const formatStrings = (template?: string, data?: any): string => {
  return (
    (template ?? "").replace(/{(\w+)}/g, (_, key) =>
      data[key] ? data[key] : ""
    ) ?? ""
  );
};

export const replaceWithJsx = (
  valueToUpdate: string,
  replacement: Record<string, JSX.Element>
) => {
  const result: Array<string | JSX.Element> = [];

  const keys = Object.keys(replacement);
  const regExp = new RegExp(keys.join("|"));

  valueToUpdate.split(regExp).forEach((item, index) => {
    result.push(item);

    const key = keys[index];
    if (valueToUpdate.includes(key)) {
      result.push(replacement[key]);
    }
  });

  return result;
};

export const getFileSizeString = (size: number): string => {
  const inKB = Math.round((size / 1000) * 10) / 10;
  const inMB = Math.round((size / 1000000) * 10) / 10;

  if (inMB < 1) return digitsToHindi(inKB.toString()) + "KB";
  else return digitsToHindi(inMB.toString()) + "MB";
};

export const getFileTypeString = (type: string): string => {
  const result = type.split("/")?.[1];
  if (!result) return type.toUpperCase();
  return result.toUpperCase();
};

export const disableButton = (...inputs: (Outputs | FileOutputs)[]) => {
  for (const input of inputs) {
    if (
      (input.required && ("value" in input ? !input.value : !input.file)) ||
      input.error
    ) {
      return true;
    }
  }
  return false;
};

export interface DateType {
  year: number;
  month: number;
  day: number;
}

const intlDateTimeFormat = new Intl.DateTimeFormat("fa-IR");

export const getJalaliDateObject = (date?: number) => {
  const stringDate = digitsToLatin(
    intlDateTimeFormat.format(date ?? Date.now())
  );
  const dateArray = stringDate.split("/");
  return {
    year: Number(dateArray[0]),
    month: Number(dateArray[1]),
    day: Number(dateArray[2]),
  };
};

export const getJalaliDateString = (jalaliDate: DateType) => {
  return (
    digitsToHindi(jalaliDate.year.toString()) +
    "/" +
    digitsToHindi(jalaliDate.month.toString()) +
    "/" +
    digitsToHindi(jalaliDate.day.toString())
  );
};

export function toGregorian(year: number, month: number, day: number): Date {
  const gDate = jalali.toGregorian(year, month, day);
  return new Date(gDate.gy, gDate.gm - 1, gDate.gd);
}

export function jalaliToEpoch(date: DateType, tillNight = false): number {
  const gregorianTime = toGregorian(date.year, date.month, date.day);
  if (tillNight)
    return new Date(gregorianTime.setHours(23, 59, 59, 999)).getTime();
  return gregorianTime.getTime();
}

export const lpad = (str: string, padStr: string, length: number) => {
  while (str.length < length) str = padStr + str;
  return str;
};

export const padZero = (number: number, length: number) => {
  return lpad(number.toString(), "0", length);
};

export function formatJalaliDate(date: DateType) {
  return digitsToHindi(
    `${padZero(date.year, 4)}/${padZero(date.month, 2)}/${padZero(date.day, 2)}`
  );
}

export function epochToDateType(timestamp: number): DateType {
  const date = jalali.toJalaali(new Date(timestamp));
  return { year: date.jy, day: date.jd, month: date.jm };
}

export function formatEpochDate(date: number) {
  try {
    return formatJalaliDate(epochToDateType(date));
  } catch (error) {
    return "";
  }
}

export const getFileFromBlob = async (
  blobUrl: string,
  fileName: string
): Promise<File> => {
  return await fetch(blobUrl)
    .then((r) => r.blob())
    .then(
      (blobFile) => new File([blobFile], fileName, { type: blobFile.type })
    );
};
const scaleDown = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
) => {
  while (width > maxWidth || height > maxHeight) {
    if (width > maxWidth) {
      const ratio = width / height;
      width = maxWidth;
      height = maxWidth / ratio;
    } else if (height > maxHeight) {
      const ratio = height / width;
      height = maxHeight;
      width = maxHeight / ratio;
    } else {
      width = maxWidth;
      height = maxWidth;
    }
  }

  return { width, height };
};

export function compressPhoto(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    // .debug("Compress: Start", file.size);
    const isCompactImg = file.size < 100_000;
    const jpgImgTypes = ["image/jpeg", "image/jpg"];
    if (isCompactImg && jpgImgTypes.includes(file.type)) {
      resolve(file);
      return;
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.src = url;
    image.onload = () => {
      const { width, height } = isCompactImg
        ? { width: image.width, height: image.height }
        : scaleDown(image.width, image.height, 1000, 1000);
      canvas.width = width;
      canvas.height = height;
      if (ctx) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, height);
        ctx?.drawImage(image, 0, 0, width, height);
      }
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // WebLogger.debug("Compress: End", blob.size);
            resolve(
              new File(
                [blob],
                /(jpg|jpeg)/.test(file.type) ? file.name : file.name + ".jpeg",
                {
                  type: "image/jpeg",
                }
              )
            );
          } else {
            reject("Blob is null");
          }
        },
        "image/jpeg",
        isCompactImg ? 1 : 0.7
      );
    };
  });
}

export const showAdDate = (date: string) => {
  return formatEpochDate(new Date(date).getTime());
};

export const showAdDateTime = (date: string) => {
  return formatEpoch(new Date(date).getTime());
};

export const detectIsSafari = () => {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const isSafari = browser.satisfies({
    macos: {
      safari: ">6.1",
    },
    mobile: {
      safari: ">=9",
      chrome: ">50",
    },
  });
  return isSafari;
};

const _getDeviceType = (): string => {
  if (!navigator.userAgent) return "unkhown";
  if (navigator.userAgent.match(/android/i)) return "android";
  if (
    navigator.userAgent.match(/iPhone/) ||
    navigator.userAgent.match(/iPod/) ||
    navigator.userAgent.match(/iPad/)
  )
    return "ios";
  return navigator.userAgent.match(/Chrome/) ||
    navigator.userAgent.match(/Firefox/) ||
    navigator.userAgent.match(/Safari/) ||
    navigator.userAgent.match(/MSIE|Trident|Edge/)
    ? "web"
    : "unkhown";
};
export const getDeviceType = (() => {
  let deviceType: string | null = null;
  return (): string => {
    if (deviceType) return deviceType;
    deviceType = _getDeviceType();
    return deviceType;
  };
})();

export const isMobile = () => {
  return ["android", "ios"].includes(getDeviceType());
};

export const useIsSafari = (() => {
  const isSafari = Boolean(detectIsSafari());
  return (): boolean => {
    return isSafari;
  };
})();

export const openLinkInSafari = (link: string) => {
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.style.display = "none";
  a.href = link;
  a.target = "_blank";
  a.click();
  document.body.removeChild(a);
};

export const getClickCountMultiplier = (date: string) => {
  if (new Date(date).getTime() < 1681041600000) {
    return 2;
  }
  return 1;
};

export function calcCtr(
  click_count: number,
  view_count: number,
  start_time: string
) {
  const click = click_count * getClickCountMultiplier(start_time);
  return click / view_count;
}
export const isCapacityFull = (used: number[], available: number[]) => {
  for (var i = 0; i < available.length; ++i) {
    if (used[i] < available[i]) return false;
  }
  return true;
};

export const isPositiveNPS = (score: number | undefined) => {
  return (score || 1) >= 7;
};

export function calcViewCountForReservation(
  viewCount: number,
  clickCount: number,
  coef: number
): number {
  return viewCount + (clickCount * coef) / 1000;
}

export const sponsoredMessageOptionToText = (
  option: SponsoredMessageOption
) => {
  const min =
    option.min_channel_members === 0 ? 0 : option.min_channel_members / 1000;
  const max =
    option.max_channel_members === -1 ? -1 : option.max_channel_members / 1000;

  if (min === 0) {
    return formatStrings(
      strings.sponsored_messages.memberSegments.less_than_max,
      {
        max: digitsToHindi(String(max)),
      }
    );
  } else if (max === -1) {
    return formatStrings(
      strings.sponsored_messages.memberSegments.more_than_min,
      {
        min: digitsToHindi(String(min)),
      }
    );
  } else {
    return formatStrings(
      strings.sponsored_messages.memberSegments.between_min_max,
      {
        min: digitsToHindi(String(min)),
        max: digitsToHindi(String(max)),
      }
    );
  }
};

export const exportToExcel = (
  data: any,
  fileName: string,
  sheetName: string
) => {
  const ws = utils.json_to_sheet(data);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, ws, sheetName);
  writeFile(workbook, `${fileName}.xlsx`, { compression: true });
};

export const openLinkInNewPage = (link: string) => {
  window.open(link, "_blank")?.focus();
};

export const isUserException = (userId: number) => {
  return MINIMUM_ORDER_EXCEPTION_USER_IDs.includes(userId);
};

export const formatNumberToPersian = (num: number): string => {
  if (num >= 1_000_000_000_000) {
    return (
      (num % 1_000_000_000_000 === 0
        ? Math.floor(num / 1_000_000_000_000)
        : (num / 1_000_000_000_000).toFixed(1)) + " تریلیون"
    );
  } else if (num >= 1_000_000_000) {
    return (
      (num % 1_000_000_000 === 0
        ? Math.floor(num / 1_000_000_000)
        : (num / 1_000_000_000).toFixed(1)) + " میلیارد"
    );
  } else if (num >= 1_000_000) {
    return (
      (num % 1_000_000 === 0
        ? Math.floor(num / 1_000_000)
        : (num / 1_000_000).toFixed(1)) + " میلیون"
    );
  } else if (num >= 1_000) {
    return (
      (num % 1_000 === 0 ? Math.floor(num / 1_000) : (num / 1_000).toFixed(1)) +
      " هزار"
    );
  } else {
    return num.toString();
  }
};

export const formatAdUrl = (url: string) => {
  let newUrl = url;
  if (newUrl.includes("ble.ir/join")) {
    newUrl = newUrl.replace(/^https?:\/\//, "");
  }
  return newUrl;
};
